import React, { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Card,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Search, Refresh } from '@mui/icons-material';
import { showSuccessMsg, showErrorMsg } from '../../../utils/toast';
import api from '../../../services/api';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import ButtonCustom from '../../../components/ButtonCustom';
import InsightTable from './InsightTable';
import ManageListDialog from './ManageListDialog';
import Pagination from '../../../components/Pagination';
import { styles } from '../../../utils/styles';
import { isNumericInput } from '../../../utils/appUtils';

const Insights = () => {
  const [openManageList, setOpenManageList] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });
  const [count, setCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  
  const [threshold, setThreshold] = useState('10');
  const [period, setPeriod] = useState('1_day');
  
  const [searchQuery, setSearchQuery] = useState('');
  
  const [appliedFilters, setAppliedFilters] = useState({
    threshold: 10,
    period: '1_day',
    searchQuery: ''
  });

  const handleSearchClick = () => {
    setAppliedFilters({
      threshold: parseFloat(threshold) || 0,
      period,
      searchQuery
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetClick = () => {
    setThreshold('10');
    setPeriod('1_day');
    setSearchQuery('');
  };

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const res = await api.post('merchant/insights/performance_metrics/', {
        period: appliedFilters.period,
        page: pagination.page,
        page_size: pagination.page_size,
        search: appliedFilters.searchQuery,
        threshold: appliedFilters.threshold
      });
      if (res && res.status === 200) {
        const responseData = res.data.results || res.data.data || res.data;
        setData(Array.isArray(responseData) ? responseData : []);
        setCount(res.data.count || 0);
      } else {
        showErrorMsg(res?.data?.error || 'Failed to load insight data');
        setData([]);
      }
    } catch (error) {
      showErrorMsg('Failed to load insight data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await api.post('merchant/insights/performance_metrics_export/', {
        period: appliedFilters.period,
        search: appliedFilters.searchQuery,
        threshold: appliedFilters.threshold
      }, {
        responseType: 'blob'
      });

      if (res && res.status === 200) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;

        let filename = `Merchant_Insights_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.xlsx`;
        const contentDisposition = res.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
          if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } else {
        showErrorMsg('Failed to export data');
      }
    } catch (error) {
      showErrorMsg('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, pagination.page, pagination.page_size]);



  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ page: 1, page_size: parseInt(event.target.value, 10) });
  };

  return (
    <Container>
      <HeaderHelpPage title="Insights" isShowLink={false} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1.5 }}>
        <ButtonCustom
          sx={{ color: styles.customButton.backgroundColor, borderColor: styles.customButton.backgroundColor, py: 0.75 }}
          variant="outlined"
          title={exporting ? 'Exporting...' : 'Export to Excel'}
          icon={exporting ? <CircularProgress size={18} color="inherit" /> : null}
          onClick={handleExport}
          disabled={exporting}
        />
        <ButtonCustom
          sx={{ ...styles.customButton, py: 0.75 }}
          variant="contained"
          title="Manage List"
          onClick={() => setOpenManageList(true)}
        />
      </Box>

      <Card>
        {/* Filters and Summary */}
        <Box p={3} sx={{ borderBottom: '1px solid #eee' }}>


          <Box display="flex" flexWrap="wrap" alignItems="flex-end" gap={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Merchant
              </Typography>
              <TextField
                size="small"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200, '& .MuiOutlinedInput-root': { height: '30px', fontSize: '14px' } }}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Volume Change
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>{'>'}</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  value={threshold}
                  onChange={(e) => {
                    if (isNumericInput(e.target.value)) {
                      setThreshold(e.target.value);
                    }
                  }}
                  sx={{ width: '80px', '& .MuiOutlinedInput-root': { height: '30px', fontSize: '14px' } }}
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>%</Typography>
              </Box>
            </Box>

            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Period
              </Typography>
              <Select
                size="small"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                sx={{ width: 150, height: '30px', '& .MuiSelect-select': { fontSize: '14px' } }}
              >
                <MenuItem value="1_day">1 Day</MenuItem>
                <MenuItem value="7_days">7 Days</MenuItem>
                <MenuItem value="30_days">30 Days</MenuItem>
              </Select>
            </Box>

            <Stack direction="row" justifyContent="flex-end" sx={{ flexGrow: 1 }}>
              <ButtonCustom
                sx={{ ml: 1, ...styles.customButton }}
                icon={<Search />}
                variant="contained"
                title="Search"
                onClick={handleSearchClick}
              />
              <ButtonCustom
                sx={{ ml: 1 }}
                icon={<Refresh />}
                title="Reset"
                onClick={handleResetClick}
              />
            </Stack>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress sx={{ color: styles.customButton.backgroundColor }} />
          </Box>
        ) : (
          <>
            <InsightTable data={data} threshold={appliedFilters.threshold} periodLabel={appliedFilters.period} />
            {data.length > 0 && (
              <Box mt={2}>
                <Pagination
                  count={count}
                  pagination={pagination}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </Box>
            )}
          </>
        )}
      </Card>

      <ManageListDialog
        open={openManageList}
        onClose={() => setOpenManageList(false)}
        fetchInsights={fetchInsights}
      />

    </Container>
  );
};

export default Insights;
